import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ChevronLeft, Clock, Calendar as CalIcon, TrendingUp, Plane, UserMinus,
  Mail, Phone, MapPin, LocateFixed, Wifi, WifiOff, Loader2, AlertTriangle,
  CheckCircle2, ChevronDown, ChevronUp, Gift, Trash2, X as XIcon, Plus, Search,
  Pencil, Filter, ShieldCheck, History, FileText, ArrowUp, ArrowDown, ArrowUpDown,
  CalendarCheck,
} from 'lucide-react';
import {
  searchWorkers, getEventsByRange, getWorkHours, getWorkplaces, listAgreements,
  getPersonAssignmentHistory, getHourBalance,
  EVENT_TYPES, EVENT_TYPE_LABELS, APPROVAL_STATUS, APPROVAL_STATUS_LABELS,
  isDeferred, deferredLabel, approverLabel, modifierLabel, parseMotivo, parseRecoverableFlag,
} from '../services/api';
import { USER_TYPE_LABELS, isSedeScoped, canManageWorker, canDeleteEvent } from '../lib/permissions';
import { useAuth } from '../contexts/AuthContext';
import { parseGeoInfo } from '../lib/geolocation';
import LocationCell from '../components/LocationCell';
import AssignCompensationModal from '../components/AssignCompensationModal';
import AssignEventModal from '../components/AssignEventModal';
import DeleteEventsConfirmModal from '../components/DeleteEventsConfirmModal';
import EventEditModal from '../components/EventEditModal';
import WorkerScheduleHistoryModal from '../components/WorkerScheduleHistoryModal';
import WorkerAgreementHistoryModal from '../components/WorkerAgreementHistoryModal';
import HourBalanceCard from '../components/HourBalanceCard';
import ComputationStartModal from '../components/ComputationStartModal';
import { useHolidays } from '../hooks/useHolidays';
import { scheduleByDateInput } from '../lib/scheduleHistory';
import {
  countPeriodsByStatus, vacationHolidayTypeFor, SICK_LEAVE_HOLIDAY_TYPE,
  filterEventsToLocalRange, isPendingStatus, isRejectedStatus, periodOverlapsRange,
} from '../lib/dayStatus';
import { yearCountWindow } from '../lib/vacationCounter';
import {
  computeDayWork, pauseWarningsByEvent, PAUSE_WARNING_LABELS, balanceHolidayYears, computeAutoBalance,
  NO_SCHEDULE_LABEL, BALANCE_EPOCH_ISO, computationStartKey, COMPUTATION_START_LABEL,
} from '../lib/hourBalance';
import {
  buildWorkerHistoryDays, sortHistoryDays, filterHistoryDays, countHistoryFilters,
  summarizeHistoryDays, reconcileWithBalance, formatSignedHm, formatHm,
  HISTORY_STATUS_FILTERS, FLAG_INFO, DAY_TYPE_LABELS, OTHER_WARNING_LABELS,
  classifyDeviation, rowHighlight, DEVIATION_THRESHOLDS, DEFAULT_DEVIATION_THRESHOLD,
} from '../utils/workerHistoryDays';
import {
  fetchEnrichedScheduleHistory, buildEnrichedScheduleHistory, balanceScheduleInput,
  balanceEventsRange, invalidateWorkerHourBalance,
} from '../hooks/useWorkerHourBalance';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, formatHmInZone, addDaysToDayKey,
  todayInZone,
} from '../lib/workerTime';

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfYearIsoStr = () => `${new Date().getFullYear()}-01-01`;

const fmtHours = (mins) => !mins ? '0h 00m' : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
// Hora de reloj del fichaje en la zona del TRABAJADOR (no la del admin).
const fmtTime = (iso, tz = DEFAULT_TIME_ZONE) => (iso ? formatHmInZone(iso, tz) || null : null);
// `key` = clave de día 'YYYY-MM-DD': se pinta en UTC para que el navegador
// no la desplace al día anterior.
const fmtDayKey = (key, opts) => new Date(`${String(key).slice(0, 10)}T00:00:00Z`).toLocaleDateString('es-ES', { ...opts, timeZone: 'UTC' });
const fmtDay = (key) => fmtDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' });
const fmtShort = (key) => fmtDayKey(key, { day: 'numeric', month: 'short' });
// 'YYYY-MM-DD' → 'dd/mm/aaaa' (aritmética de cadena, sin zona).
const fmtDmy = (key) => (key ? `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}` : '');

// Historial día a día: filas por página ("Mostrar más").
const HISTORY_PAGE_SIZE = 100;
// Orden por defecto al pulsar cada cabecera: fecha más reciente primero,
// trabajado de más a menos y diferencia de más negativa a más positiva (lo
// útil para ver por qué falla la bolsa).
const DEFAULT_SORT_DIR = { date: 'desc', worked: 'desc', balance: 'asc' };
const FLAG_BADGE_CLS = {
  sinCerrar: 'bg-danger/10 text-danger',
  sinAbrir: 'bg-orange-100 text-orange-800',
  sinFichar: 'bg-danger text-white',
  porAprobar: 'bg-amber-100 text-amber-800',
  otras: 'bg-slate-200 text-ink',
  exceso: 'bg-sky-100 text-sky-800',
  defecto: 'bg-rose-100 text-rose-800',
};
// Resaltado suave de la fila (rowHighlight en workerHistoryDays.js). Fondo
// tenue + franja izquierda algo más fuerte (sombra inset: no mueve el layout).
// Nunca es la única señal: los badges de la columna Estado lo repiten.
// Precedencia: abierto (sin cerrar / sin abrir) > no llega > se pasa.
const ROW_TINT = {
  abierto: { row: 'bg-amber-50 hover:bg-amber-100', bar: 'shadow-[inset_4px_0_0_0_#fbbf24]' }, // amber-400
  defecto: { row: 'bg-rose-50 hover:bg-rose-100', bar: 'shadow-[inset_4px_0_0_0_#fb7185]' }, // rose-400
  exceso: { row: 'bg-sky-50 hover:bg-sky-100', bar: 'shadow-[inset_4px_0_0_0_#38bdf8]' }, // sky-400
};
const fmtThreshold = (m) => (m % 60 === 0 ? `${m / 60} h` : `${m} min`);
// Umbral de desviación recordado por usuario/navegador (comodidad; si el
// almacenamiento no está disponible se usa el de por defecto).
const DEVIATION_STORAGE_KEY = 'temcontrol.workerHistory.deviationThreshold';
const readStoredThreshold = () => {
  try {
    const n = Number(window.localStorage.getItem(DEVIATION_STORAGE_KEY));
    return DEVIATION_THRESHOLDS.includes(n) ? n : DEFAULT_DEVIATION_THRESHOLD;
  } catch {
    return DEFAULT_DEVIATION_THRESHOLD;
  }
};
const storeThreshold = (n) => {
  try { window.localStorage.setItem(DEVIATION_STORAGE_KEY, String(n)); } catch { /* sin almacenamiento */ }
};
// Cabecera de columna ordenable (botón accesible con la flecha del orden).
const SortHeader = ({ label, active, dir, onClick, title, align = 'left' }) => {
  let Icon = ArrowUpDown;
  if (active) Icon = dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold hover:text-ink transition-colors ${
        align === 'right' ? 'flex-row-reverse' : ''
      } ${active ? 'text-primary-darker' : ''}`}
    >
      {label}
      <Icon size={11} className={active ? '' : 'opacity-40'} />
    </button>
  );
};

const balanceCls = (m) => {
  if (m == null) return 'text-ink-muted font-normal';
  if (m < 0) return 'text-danger';
  if (m > 0) return 'text-success';
  return 'text-ink-muted';
};

// Resumen por día. Trabajado y pausas salen de computeDayWork
// (src/lib/hourBalance.js): NINGUNA pausa cuenta como trabajada, todas se
// restan (decisión de producto 2026-09-21), igual que la bolsa. Los fichajes
// rechazados no cuentan. Hay que pasarle TODOS los eventos del día: los
// filtros del historial solo deciden qué filas se ven, nunca los totales.
// `tz`: zona del trabajador — decide a qué día pertenece cada fichaje.
const groupEventsByDay = (events, tz = DEFAULT_TIME_ZONE) => {
  const map = new Map();
  for (const e of events) {
    const fecha = dayKeyInZone(e.timestamp, tz);
    if (!fecha) continue;
    if (!map.has(fecha)) map.set(fecha, { fecha, ins: [], outs: [], events: [] });
    const day = map.get(fecha);
    day.events.push(e);
    if (e.eventType === EVENT_TYPES.CLOCK_IN) day.ins.push(e.timestamp);
    else if (e.eventType === EVENT_TYPES.CLOCK_OUT) day.outs.push(e.timestamp);
  }
  return Array.from(map.values()).map((d) => {
    d.ins.sort(); d.outs.sort();
    d.entrada = d.ins[0] || null;
    d.salida = d.outs[d.outs.length - 1] || null;
    d.work = computeDayWork(d.events, { timeZone: tz });
    d.totalMinutes = d.work.workedMinutes;
    d.pauseMinutes = d.work.pauseMinutes;
    d.pauseWarnings = pauseWarningsByEvent(d.work);
    d.clockEvents = d.events
      .filter((e) => e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return d;
  }).sort((a, b) => b.fecha.localeCompare(a.fecha));
};

// Tipos que pasan por aprobación del admin. Para estos (y para cualquier
// evento diferido) mostramos los chips de "Aprobado por X" / "Editado por X"
// con la traza embebida en geoInfo (workaround del backend, ver api.js).
const SUPERVISED_TYPES = new Set([
  EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END,
  EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END,
  EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END,
  EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END,
]);

const pairPeriods = (events, startType, endType) => {
  const sorted = events
    .filter((e) => e.eventType === startType || e.eventType === endType)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const periods = [];
  let pending = null;
  for (const e of sorted) {
    if (e.eventType === startType) pending = e;
    else if (e.eventType === endType && pending) {
      // IMPORTANTE: el approvalStatus del periodo lo tomamos del START.
      // Razón: el flujo de aprobación habitualmente aprueba el START primero
      // (que dispara el "cuento como vacación") y el END puede quedar
      // pendiente unos minutos/horas. Para mantenerse coherente con la
      // tabla comparativa de equipo (que pinta el periodo si el START
      // está aprobado), el contador hace lo mismo: filtra por START.
      periods.push({
        start: pending.timestamp,
        end: e.timestamp,
        startEvent: pending,
        endEvent: e,
        approvalStatus: pending.approvalStatus,
      });
      pending = null;
    }
  }
  if (pending) {
    periods.push({
      start: pending.timestamp, end: pending.timestamp, open: true,
      startEvent: pending,
      approvalStatus: pending.approvalStatus,
    });
  }
  return periods;
};

const todayIsoStr = () => isoDate(new Date());
const monthsAgoIsoStr = (months) => {
  const d = new Date(); d.setMonth(d.getMonth() - months); return isoDate(d);
};

// El backend ANTIGUO trataba endDate como medianoche (00:00Z, exclusivo);
// pedimos 1 día más para incluir el día final completo. El backend actual ya
// es inclusivo, así que el resultado se recorta en local al rango pedido
// (filterEventsToLocalRange) para no mostrar un día de más. Aritmética pura
// de claves de día (sin zona del navegador).
const nextDayIso = (iso) => (iso ? addDaysToDayKey(iso, 1) : iso);

const WorkerDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const workerId = Number(id);
  const [worker, setWorker] = useState(null);
  // Zona del trabajador de la ficha (Canarias / península). La fila de
  // searchWorkers trae autonomousCommunity/province; sin datos → Madrid.
  const tz = useMemo(() => timeZoneForWorker(worker), [worker]);
  const tzHint = timeZoneLabel(tz);
  const [workplace, setWorkplace] = useState(null);
  const [events, setEvents] = useState([]);
  // Para el cómputo de la bolsa de horas necesitamos los workhours del
  // trabajador. Los cargamos junto al resto en el load principal.
  const [workhours, setWorkhours] = useState([]);
  // Y también traemos los eventos del AÑO COMPLETO para que el balance
  // automático refleje toda la historia, no solo el rango del filtro de
  // historial visible (que por defecto es 1 mes).
  const [allYearEvents, setAllYearEvents] = useState([]);
  // Si falla la carga de allYearEvents, la bolsa NO puede dar un total fiable:
  // HourBalanceCard muestra error (mismo criterio que la app).
  const [allYearEventsError, setAllYearEventsError] = useState(null);
  // Eventos POSTERIORES al rango de la bolsa (1-ene → 1-mar del año que
  // viene, yearCountWindow): solo para emparejar los periodos del año en
  // curso que terminan en el siguiente (28-dic → 3-ene). NO entran en la
  // bolsa (su cómputo depende del último evento cargado).
  const [tailEvents, setTailEvents] = useState([]);
  // Fallo de GET workhours: el horario NO se da por vacío (los contadores
  // de vacaciones / días completos muestran "—").
  const [workhoursFailed, setWorkhoursFailed] = useState(false);
  // Convenios del sistema: los necesitamos para saber el `holidayType`
  // ('laborables' | 'festivos') del convenio del trabajador y poder
  // contar los días de vacaciones según la modalidad correcta.
  const [agreements, setAgreements] = useState([]);

  // Histórico de asignaciones de convenio del trabajador (PersonAssignment).
  // Cada entrada trae los datos del convenio joineados: assignAgreementName,
  // assignMonthlyHours, assignVacationDays, assignHolidayType.
  // Si todavía no se ha hecho el seed en backend, el array viene vacío y
  // el resto de la página sigue usando el campo legacy `worker.agreement`.
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  // Ajustes manuales de bolsa (/api/hourBalance). Aquí SOLO nos interesan para
  // sacar la FECHA DE INICIO DE CÓMPUTO del trabajador (la fila marcadora
  // "Inicio de computo", ver src/lib/hourBalance.js): el historial día a día y
  // la conciliación tienen que aplicar el mismo corte que la bolsa.
  // HourBalanceCard carga su propia copia para pintar la lista de ajustes;
  // `balanceRefresh` la obliga a recargar cuando cambiamos la fecha desde aquí.
  const [balanceOffsets, setBalanceOffsets] = useState([]);
  const [balanceRefresh, setBalanceRefresh] = useState(0);
  const computationStart = useMemo(() => computationStartKey(balanceOffsets), [balanceOffsets]);
  const [computationStartOpen, setComputationStartOpen] = useState(false);

  const reloadBalanceOffsets = useCallback(async () => {
    if (!workerId) { setBalanceOffsets([]); return; }
    try {
      const list = await getHourBalance(workerId);
      setBalanceOffsets(Array.isArray(list) ? list : []);
    } catch {
      // Best-effort: sin los ajustes seguimos pintando el historial, solo que
      // sin el corte por fecha de alta (comportamiento de siempre).
      setBalanceOffsets([]);
    }
  }, [workerId]);

  useEffect(() => { reloadBalanceOffsets(); }, [reloadBalanceOffsets]);

  // Histórico de versiones de horario (ScheduleVersion). Cada entrada incluye
  // los 7 slots de esa versión en `assignSlots`. Igual que con los convenios:
  // si está vacío, fallback al getWorkHours() legacy de toda la vida.
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // Modal de SOLO LECTURA con el histórico completo de jornadas que ha
  // tenido el trabajador. Disponible para cualquier admin que pueda ver la
  // ficha — no requiere permiso de gestión, solo de visualización.
  const [scheduleHistoryOpen, setScheduleHistoryOpen] = useState(false);
  // Modal de histórico de CONVENIOS. A diferencia del de jornadas, este
  // permite ALTA con periodo cerrado (validFrom + validTo) usando el endpoint
  // /api/personAssignment/assign-bounded — útil para meter tramos históricos.
  // El borrado también está habilitado. La edición la haremos cuando el
  // backend exponga PUT. Solo se ofrece el botón a quien puede gestionar.
  const [agreementHistoryOpen, setAgreementHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros de historial — DOS estados:
  //   · draftStart/draftEnd → lo que el admin está escribiendo en los inputs.
  //   · histStart/histEnd   → lo que efectivamente se mandó al backend.
  // Antes existía un solo par y `load` se reejecutaba en cada keystroke del
  // input date → la página parpadeaba constantemente. Ahora hay que pulsar
  // "Buscar" para aplicar el rango. Los botones quick-range (1 mes, 3 meses…)
  // siguen aplicando directo porque son acciones explícitas con un solo click.
  const [histStart, setHistStart] = useState(startOfYearIsoStr());
  const [histEnd, setHistEnd] = useState(todayIsoStr());
  const [draftStart, setDraftStart] = useState(startOfYearIsoStr());
  const [draftEnd, setDraftEnd] = useState(todayIsoStr());
  const [expandedDay, setExpandedDay] = useState(null);

  // Multi-select de fichajes para borrado masivo. Guardamos IDs (números).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Eventos puntuales abiertos en el modal (puede ser la selección masiva o
  // un par individual lanzado desde el panel de vacaciones/bajas).
  const [pendingDelete, setPendingDelete] = useState([]);

  // Modal de edición de un único evento. Lo abrimos pasando el evento
  // completo; el modal hace el PUT y al volver con éxito reemplazamos
  // ese evento en el array `events` para que la UI refleje el cambio
  // sin esperar a un reload.
  const [editingEvent, setEditingEvent] = useState(null);
  const handleEdited = (updated) => {
    if (!updated?.id) return;
    const patch = (prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e));
    setEvents(patch);
    // La bolsa (HourBalanceCard) y los contadores se calculan sobre
    // allYearEvents: sin esto el saldo seguía con el evento antiguo hasta
    // recargar la página.
    setAllYearEvents(patch);
    setTailEvents(patch);
    invalidateWorkerHourBalance(workerId);
  };

  // Filtros del historial — además del rango de fechas, dos filtros más:
  //   · typeGroups: Set con grupos activos ('clock', 'pause', 'vacation',
  //     'sick', 'compensation', 'absence'). Si está vacío → mostrar todos.
  //   · approvalFilter: 'todos' | '0' | '1' | '2' (PENDING/APPROVED/REJECTED)
  //   · deferredFilter: 'todos' | 'normal' | 'diferido'
  // Default: jornada + pausas ('clock' y 'pause') — las pausas se restan del
  // total, así que tienen que verse siempre. El admin puede ampliar a otros
  // tipos sin perder la agrupación diaria. Los filtros SOLO deciden qué filas
  // se ven; los totales se calculan siempre con todos los eventos del día.
  const DEFAULT_TYPE_GROUPS = ['clock', 'pause'];
  const [typeGroups, setTypeGroups] = useState(() => new Set(DEFAULT_TYPE_GROUPS));
  const [approvalFilter, setApprovalFilter] = useState('todos');
  const [deferredFilter, setDeferredFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  // Historial día a día: filtro de estado (chips, selección única), orden y
  // paginación ligera.
  const [statusFilter, setStatusFilter] = useState('todos');
  // Desviación (min) a partir de la cual un día "se pasa" / "no llega".
  const [deviationThreshold, setDeviationThresholdState] = useState(readStoredThreshold);
  const setDeviationThreshold = (n) => { setDeviationThresholdState(n); storeThreshold(n); };
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);
  const toggleSort = (key) => setSort((prev) => (prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: DEFAULT_SORT_DIR[key] || 'desc' }));

  const toggleTypeGroup = (g) => setTypeGroups((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  const canDelete = canDeleteEvent(user, worker);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBulkDelete = () => {
    const list = events.filter((e) => selectedIds.has(e.id));
    if (!list.length) return;
    setPendingDelete(list);
    setDeleteOpen(true);
  };

  const openSingleDelete = (eventList) => {
    setPendingDelete(eventList);
    setDeleteOpen(true);
  };

  const handleDeleted = (deletedIds) => {
    if (!deletedIds?.length) return;
    setEvents((prev) => prev.filter((e) => !deletedIds.includes(e.id)));
    // Mismo motivo que en handleEdited: la bolsa va sobre allYearEvents.
    setAllYearEvents((prev) => prev.filter((e) => !deletedIds.includes(e.id)));
    setTailEvents((prev) => prev.filter((e) => !deletedIds.includes(e.id)));
    invalidateWorkerHourBalance(workerId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of deletedIds) next.delete(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Cada carga de la ficha invalida la bolsa cacheada de este trabajador
    // (la del chip de Equipo): así, al volver a la lista, se recalcula con
    // lo que se haya tocado aquí (eventos, ajustes, horario).
    invalidateWorkerHourBalance(workerId);
    // Zona del trabajador para esta carga (el estado `tz` aún no está al día).
    let workerTz = timeZoneForWorker(null);
    try {
      const all = await searchWorkers('');
      const found = Array.isArray(all) ? all.find((w) => w.id === workerId) : null;
      workerTz = timeZoneForWorker(found);
      setWorker(found || null);
      if (found?.companyId && found?.workingSite != null) {
        try {
          const wps = await getWorkplaces(found.companyId);
          const wp = (Array.isArray(wps) ? wps : []).find((w) => Number(w.id) === Number(found.workingSite));
          setWorkplace(wp || null);
        } catch { setWorkplace(null); }
      } else {
        setWorkplace(null);
      }
    } catch (err) {
      setError(err);
    }
    try {
      // start - 1: el día de Madrid empieza a las 22:00/23:00 UTC del anterior.
      const data = await getEventsByRange(workerId, addDaysToDayKey(histStart, -1) || histStart, nextDayIso(histEnd));
      setEvents(filterEventsToLocalRange(data, histStart, histEnd, workerTz));
    } catch (err) {
      setError(err);
      setEvents([]);
    }
    // Workhours + año completo de eventos → para el cómputo de la bolsa.
    // Los corremos en paralelo y absorbemos errores: si fallan, el card
    // de bolsa muestra balance 0 pero el resto de la página sigue funcionando.
    try {
      const wh = await getWorkHours(workerId);
      setWorkhours(Array.isArray(wh) ? wh : []);
      setWorkhoursFailed(false);
    } catch { setWorkhours([]); setWorkhoursFailed(true); }
    try {
      const ags = await listAgreements();
      setAgreements(Array.isArray(ags) ? ags : []);
    } catch { setAgreements([]); }
    // Histórico de convenios (PersonAssignment). Si el seed no se ha lanzado
    // todavía en backend, el endpoint puede devolver array vacío o 404 —
    // toleramos ambos casos y dejamos que el resto de la página caiga al
    // fallback del campo legacy `worker.agreement`.
    try {
      const history = await getPersonAssignmentHistory(workerId);
      setAssignmentHistory(Array.isArray(history) ? history : []);
    } catch { setAssignmentHistory([]); }
    // Histórico de versiones de horario (ScheduleVersion). Mismo trato:
    // si está vacío, el resto de la página seguirá usando getWorkHours()
    // estándar (los 7 slots de la versión activa actual).
    // Cada versión se enriquece con SUS slots reales (no con el horario
    // actual): la bolsa computa cada día con el horario que estaba vigente
    // entonces. Helper compartido con InformesPage / Equipo (ver
    // src/hooks/useWorkerHourBalance.js).
    try {
      setScheduleHistory(await fetchEnrichedScheduleHistory(workerId));
    } catch { setScheduleHistory([]); }
    try {
      // Rango: 1 ene del año pasado hasta 31 dic del año actual. Tenemos
      // que incluir el FUTURO del año en curso porque vacaciones y bajas
      // se planifican con meses de antelación: si solo cargáramos hasta
      // hoy, una vacación de agosto pedida en mayo no aparecería en el
      // contador del Equipo (mostraba 3 cuando había 15 días al año).
      // Es el MISMO rango que usa el cómputo compartido de la bolsa.
      // Mismo margen de -1 día que el cómputo compartido (useWorkerHourBalance).
      const { start: yearStart, end: yearEnd } = balanceEventsRange(new Date(), workerTz);
      const allEv = await getEventsByRange(workerId, addDaysToDayKey(yearStart, -1), yearEnd);
      setAllYearEvents(Array.isArray(allEv) ? allEv : []);
      setAllYearEventsError(null);
      // Cola del año (hasta el 1-mar siguiente) para los periodos que cruzan
      // el cambio de año. Best-effort: sin ella solo se pierde ese caso.
      try {
        const tailWin = yearCountWindow(Number(String(yearEnd).slice(0, 4)));
        const tail = await getEventsByRange(workerId, addDaysToDayKey(yearEnd, 1), tailWin.fetchTo);
        setTailEvents(Array.isArray(tail) ? tail : []);
      } catch { setTailEvents([]); }
    } catch (err) {
      setAllYearEvents([]);
      setTailEvents([]);
      setAllYearEventsError(err || new Error('No se pudieron cargar los eventos'));
    }
    setLoading(false);
  }, [workerId, histStart, histEnd]);

  const applyQuickRange = (months) => {
    const start = months === 0 ? '2000-01-01' : monthsAgoIsoStr(months);
    const end = todayIsoStr();
    // Quick-ranges aplican directo: sincronizan draft Y applied.
    setHistStart(start);
    setHistEnd(end);
    setDraftStart(start);
    setDraftEnd(end);
  };

  // El botón "Buscar" promociona el draft a applied. Esto es lo que
  // dispara realmente el reload. Si el draft coincide con el applied
  // actual, el botón queda inhabilitado (no hay nada que aplicar).
  const applyDraftRange = () => {
    setHistStart(draftStart);
    setHistEnd(draftEnd);
  };
  const draftDirty = draftStart !== histStart || draftEnd !== histEnd;

  useEffect(() => { load(); }, [load]);

  // Guard: admin de sede / overseer no puede ver un worker de otra sede
  const forbiddenByScope = useMemo(() => {
    if (!user || !worker) return false;
    if (!isSedeScoped(user.type)) return false;
    const userWs = user.workingSite != null ? String(user.workingSite) : null;
    const workerWs = worker.workingSite != null ? String(worker.workingSite) : null;
    if (!userWs) return false;
    return workerWs !== userWs && worker.id !== user.id;
  }, [user, worker]);

  // Workaround para dos limitaciones del backend / del modelo de datos:
  //
  // 1. GET /api/scheduleVersion/history/:id NO joinea los assignSlots de
  //    cada versión (bug pendiente). Sin slots, el cómputo trataba la
  //    jornada esperada como 0 cada día y todo se sumaba como extras
  //    (caso Aurora con +928h falsas).
  //
  // 2. Los fichajes pueden ser ANTERIORES a la primera ScheduleVersion
  //    del trabajador. P.ej. Antonio ficha desde 2025-10 pero la versión
  //    vigente solo empieza 2026-01. Esos meses no tenían slot y todo
  //    el trabajo se sumaba como extras (+432h falsas).
  //
  // La estrategia es la misma para los dos: rellenar assignSlots con el
  // workhours actual cuando falten, y añadir una "versión virtual" al
  // final del array que cubra cualquier fecha no cubierta por las reales.
  // findActiveEntry itera en orden y la real gana si coincide, así que la
  // virtual solo actúa de fallback.
  // (La lógica vive en buildEnrichedScheduleHistory, compartida con
  // InformesPage y el chip de Equipo para que no puedan divergir.)
  const enrichedScheduleHistory = useMemo(
    () => buildEnrichedScheduleHistory(scheduleHistory, workhours),
    [scheduleHistory, workhours]
  );

  // Horario del trabajador por fecha (contadores de vacaciones/bajas). Ya NO
  // se usa para las pausas: todas se restan. Dos modos según si tenemos
  // histórico de versiones o no:
  //   · Si tenemos histórico (ya enriquecido con los slots actuales)
  //     pasamos el array completo, y los consumidores resuelven la
  //     versión vigente en cada fecha.
  //   · Si está vacío (seed no lanzado o trabajador sin versiones) →
  //     fallback al Map<dow, slot> de los workhours legacy.
  // (scheduleByDateInput, compartido con la app y los calendarios.)
  const workHoursByDow = useMemo(
    () => scheduleByDateInput(enrichedScheduleHistory, workhours),
    [enrichedScheduleHistory, workhours]
  );

  // `events` ya viene filtrado por el rango [histStart, histEnd] (la API
  // se llama con esas fechas), así que las stats se calculan directamente
  // sobre todo el array sin segundo filtrado. Eliminamos el selector
  // "semana / mes / año" — el único filtro es el de fechas de la tabla y
  // aplica a TODO: horas, días fichados, historial de fichajes.
  // Días con sus totales calculados sobre TODOS los eventos (sin filtros):
  // la cabecera y la columna Total de la tabla salen de aquí.
  const allDays = useMemo(() => groupEventsByDay(events, tz), [events, tz]);
  const stats = useMemo(() => {
    const totalMinutes = allDays.reduce((s, d) => s + d.totalMinutes, 0);
    const pauseMinutes = allDays.reduce((s, d) => s + d.pauseMinutes, 0);
    const workedDays = allDays.filter((d) => d.ins.length).length;
    const completeDays = allDays.filter((d) => d.ins.length && d.outs.length).length;
    return { totalMinutes, pauseMinutes, workedDays, completeDays };
  }, [allDays]);

  // Vacaciones y bajas se emparejan sobre TODO el año del trabajador, no
  // sobre `events` (filtrado por el rango de fechas del usuario). Si solo
  // mirásemos `events`, una vacación con START fuera del rango quedaría
  // huérfana y el contador mentiría — por eso siempre cargamos el año
  // completo en allYearEvents para los emparejamientos largos.
  // + tailEvents (hasta el 1-mar del año que viene) para que los periodos que
  // cruzan el cambio de año se emparejen enteros.
  const periodEvents = useMemo(() => [...allYearEvents, ...tailEvents], [allYearEvents, tailEvents]);
  const vacationPeriods = useMemo(() => pairPeriods(periodEvents, EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END), [periodEvents]);
  const sickPeriods = useMemo(() => pairPeriods(periodEvents, EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END), [periodEvents]);
  // Los CONTADORES son del AÑO EN CURSO del trabajador. Antes sumaban los dos
  // años cargados (el pasado y este) bajo una sola etiqueta "Vacaciones".
  // Solo cuentan los días dentro de [countYear-01-01, countYear-12-31]
  // (reparto por año compartido: countPeriods con from/to).
  const countYear = Number(todayInZone(tz).slice(0, 4));
  const countWindow = useMemo(() => yearCountWindow(countYear), [countYear]);
  const periodInCountYear = useCallback(
    (p) => periodOverlapsRange(p, countWindow.from, countWindow.to, tz),
    [countWindow, tz]
  );

  // Festivos del trabajador (según su comunidad autónoma) para el año actual.
  // Los descontamos del cómputo de vacaciones/bajas para que el contador
  // refleje los DÍAS REALES de descanso solicitados (sin contar festivos
  // que ya eran no laborables de por sí).
  // Años: TODOS los que abarca el cómputo de la bolsa (balanceHolidayYears,
  // desde el epoch 2026 hasta hoy). Con solo el año actual, a partir de 2027
  // los festivos de 2026 contaban como faltas. Igual que la app.
  const { holidays: workerHolidays } = useHolidays(worker, balanceHolidayYears(new Date(), tz));
  // Festivos para el HISTORIAL: los mismos años de la bolsa + los del rango
  // visible (desde 2020, hasta el año en curso), para que los días con
  // fichajes de años anteriores tampoco esperen jornada en festivo. Dentro del
  // periodo de la bolsa el conjunto es idéntico al de la bolsa (misma carga).
  const historyHolidayYears = useMemo(() => {
    const set = new Set(balanceHolidayYears(new Date(), tz));
    const current = new Date().getFullYear();
    const from = Math.max(2020, Number(String(histStart).slice(0, 4)) || current);
    const to = Math.min(current, Number(String(histEnd).slice(0, 4)) || current);
    for (let y = from; y <= to; y += 1) set.add(y);
    return Array.from(set).sort();
  }, [tz, histStart, histEnd]);
  const { holidays: extraHistoryHolidays } = useHolidays(worker, historyHolidayYears);
  const historyHolidays = useMemo(
    () => new Set([...workerHolidays, ...extraHistoryHolidays]),
    [workerHolidays, extraHistoryHolidays]
  );

  // Tipo de cómputo de vacaciones según el convenio del trabajador.
  // 'laborables' (default) o 'festivos' (naturales: cuentan findes/festivos).
  const workerAgreement = useMemo(
    () => agreements.find((a) => Number(a.id) === Number(worker?.agreement)) || null,
    [agreements, worker]
  );
  const holidayType = vacationHolidayTypeFor(workerAgreement);

  // Cuenta de días "consumidos" delegando en el helper compartido con la app
  // (src/lib/dayStatus.js → countPeriodsByStatus), que aplica la regla según
  // `holidayType` (ver src/lib/vacationCounter.js). El estado del periodo es
  // el de su START (ver pairPeriods):
  //   · Aprobados  → cuentan como días consumidos.
  //   · Pendientes → ni aprobados ni rechazados; se muestran aparte.
  //   · Rechazados → se descartan de TODO. Antes "pendientes" se calculaba
  //     como total − aprobados y los rechazados salían como pendientes.
  //   · Solo días del año en curso (from/to); los periodos listados en el
  //     resumen son los que tocan ese año.
  //   · Sin horario (petición fallida / sin configurar) → "—" en vez de un
  //     número (scheduleUnknown: sin horario no hay arrastre ni laborables).
  const vacationCounts = useMemo(
    () => countPeriodsByStatus(vacationPeriods, {
      holidayType,
      workHoursByDow,
      holidaysSet: workerHolidays,
      timeZone: tz,
      from: countWindow.from,
      to: countWindow.to,
    }),
    [vacationPeriods, holidayType, workHoursByDow, workerHolidays, tz, countWindow]
  );
  const approvedVacationPeriods = vacationCounts.approved.periods.filter(periodInCountYear);
  const pendingVacationPeriods = vacationCounts.pending.periods.filter(periodInCountYear);
  const vacationCountsReady = !workhoursFailed && !vacationCounts.scheduleUnknown;
  const vacationDays = vacationCountsReady ? vacationCounts.approved.count : '—';
  // Las bajas médicas se computan SIEMPRE en modo 'festivos' (naturales):
  // una baja médica no se "interrumpe" porque sea fin de semana — la baja
  // dura los días que dura. Es la regla que aplica el INSS.
  const sickCounts = useMemo(
    () => countPeriodsByStatus(sickPeriods, {
      holidayType: SICK_LEAVE_HOLIDAY_TYPE,
      workHoursByDow,
      holidaysSet: workerHolidays,
      timeZone: tz,
      from: countWindow.from,
      to: countWindow.to,
    }),
    [sickPeriods, workHoursByDow, workerHolidays, tz, countWindow]
  );
  const approvedSickPeriods = sickCounts.approved.periods.filter(periodInCountYear);
  const pendingSickPeriods = sickCounts.pending.periods.filter(periodInCountYear);
  const sickCountsReady = !workhoursFailed && !sickCounts.scheduleUnknown;
  const sickDays = sickCountsReady ? sickCounts.approved.count : '—';

  // Aplica los filtros de tipo / estado / diferido sobre el listado raw de
  // eventos. Solo deciden QUÉ FILAS se ven (días con algún evento que pase el
  // filtro y eventos del detalle expandido). Los totales del día NO dependen
  // de los filtros: antes, con el filtro por defecto (solo jornada) las pausas
  // se quitaban ANTES de calcular y el total no las restaba.
  const eventTypeMatchesGroup = (eventType) => {
    if (!typeGroups.size) return true;
    if (typeGroups.has('clock') && (eventType === EVENT_TYPES.CLOCK_IN || eventType === EVENT_TYPES.CLOCK_OUT)) return true;
    if (typeGroups.has('pause') && (eventType === EVENT_TYPES.PAUSE_START || eventType === EVENT_TYPES.PAUSE_END)) return true;
    if (typeGroups.has('vacation') && (eventType === EVENT_TYPES.VACATION_START || eventType === EVENT_TYPES.VACATION_END)) return true;
    if (typeGroups.has('sick') && (eventType === EVENT_TYPES.SICK_LEAVE_START || eventType === EVENT_TYPES.SICK_LEAVE_END)) return true;
    if (typeGroups.has('compensation') && (eventType === EVENT_TYPES.COMPENSATION_START || eventType === EVENT_TYPES.COMPENSATION_END)) return true;
    if (typeGroups.has('absence') && (eventType === EVENT_TYPES.ABSENCE_START || eventType === EVENT_TYPES.ABSENCE_END)) return true;
    return false;
  };

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (!eventTypeMatchesGroup(Number(e.eventType))) return false;
      if (approvalFilter !== 'todos' && Number(e.approvalStatus ?? APPROVAL_STATUS.APPROVED) !== Number(approvalFilter)) return false;
      if (deferredFilter === 'diferido' && !isDeferred(e)) return false;
      if (deferredFilter === 'normal' && isDeferred(e)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, typeGroups, approvalFilter, deferredFilter]);

  // Indica si hay filtros distintos del default (typeGroups = {'clock',
  // 'pause'}, approval/deferred = 'todos'). Usado para mostrar contador en el
  // botón y un atajo de "Limpiar".
  const filtersActive =
    !(typeGroups.size === DEFAULT_TYPE_GROUPS.length && DEFAULT_TYPE_GROUPS.every((g) => typeGroups.has(g)))
    || approvalFilter !== 'todos'
    || deferredFilter !== 'todos';

  const resetFilters = () => {
    setTypeGroups(new Set(DEFAULT_TYPE_GROUPS));
    setApprovalFilter('todos');
    setDeferredFilter('todos');
  };

  // ── Historial DÍA A DÍA (src/utils/workerHistoryDays.js) ────────────────
  // Una fila por día del rango: días con eventos + días laborables sin fichar.
  // Jornada esperada y diferencia con las MISMAS reglas que la bolsa
  // (mismos eventos del cómputo, mismo horario, mismos festivos, misma zona).
  const scheduleInput = useMemo(
    () => balanceScheduleInput(enrichedScheduleHistory, workhours),
    [enrichedScheduleHistory, workhours]
  );
  const history = useMemo(() => buildWorkerHistoryDays({
    rangeEvents: events,
    balanceEvents: allYearEventsError ? [] : allYearEvents,
    scheduleInput,
    holidaysSet: historyHolidays,
    timeZone: tz,
    startKey: histStart,
    endKey: histEnd,
    computationStart,
  }), [events, allYearEvents, allYearEventsError, scheduleInput, historyHolidays, tz, histStart, histEnd, computationStart]);

  // Bolsa automática (sin ajustes manuales) para la línea de conciliación:
  // mismo cálculo que HourBalanceCard (mismos eventos, horario y festivos).
  const bolsaAuto = useMemo(() => {
    if (allYearEventsError || history.noSchedule || !history.balancePeriod) return null;
    const auto = computeAutoBalance(allYearEvents, scheduleInput, workerHolidays, tz, computationStart);
    if (auto.noSchedule || auto.totalMinutes == null) return null;
    return { ...history.balancePeriod, totalMinutes: auto.totalMinutes, byDay: auto.byDay };
  }, [allYearEvents, allYearEventsError, scheduleInput, workerHolidays, tz, history, computationStart]);
  const reconciliation = useMemo(
    () => reconcileWithBalance(history.rows, bolsaAuto, histStart, histEnd),
    [history, bolsaAuto, histStart, histEnd]
  );

  // Filtro de TIPO / estado de aprobación / modo: decide qué EVENTOS se ven
  // dentro de cada día (y oculta los días sin ningún evento que pase), nunca
  // los totales. Los días "Sin fichar" se ven siempre salvo que se filtre por
  // estado de aprobación o modo (no tienen fichajes que cumplan eso).
  const typeVisibleRows = useMemo(() => {
    const visible = new Set(filteredEvents.map((e) => dayKeyInZone(e.timestamp, tz)));
    const showMissing = approvalFilter === 'todos' && deferredFilter === 'todos';
    return history.rows.filter((r) => visible.has(r.date) || (showMissing && r.flags.sinFichar));
  }, [history, filteredEvents, tz, approvalFilter, deferredFilter]);
  const deviationOpts = useMemo(() => ({ threshold: deviationThreshold }), [deviationThreshold]);
  const statusCounts = useMemo(() => countHistoryFilters(typeVisibleRows, deviationOpts), [typeVisibleRows, deviationOpts]);
  const statusRows = useMemo(() => filterHistoryDays(typeVisibleRows, statusFilter, deviationOpts), [typeVisibleRows, statusFilter, deviationOpts]);
  const sortedRows = useMemo(() => sortHistoryDays(statusRows, sort), [statusRows, sort]);
  const days = sortedRows.slice(0, visibleCount);
  const visibleSummary = useMemo(() => summarizeHistoryDays(statusRows, deviationOpts), [statusRows, deviationOpts]);
  // Al cambiar filtros, orden o rango se vuelve a la primera página.
  useEffect(() => { setVisibleCount(HISTORY_PAGE_SIZE); }, [statusFilter, sort, histStart, histEnd, typeGroups, approvalFilter, deferredFilter, deviationThreshold]);

  // "Ver periodo de la bolsa": aplica como rango del historial el periodo de
  // cómputo de la bolsa (primer fichaje/epoch → ayer o último evento).
  const applyBalancePeriod = () => {
    if (!history.balancePeriod) return;
    const { startKey, endKey } = history.balancePeriod;
    setHistStart(startKey);
    setHistEnd(endKey);
    setDraftStart(startKey);
    setDraftEnd(endKey);
  };

  // Eventos del día expandido FILTRADOS (no solo CLOCK_IN/OUT). Lo
  // calculamos por separado porque la tabla muestra Entrada/Salida basadas
  // en clockEvents — pero en el detalle expandido queremos enseñar también
  // pausas, vacaciones, etc. si el admin las activó en los filtros.
  const expandedDayEvents = useMemo(() => {
    if (!expandedDay) return [];
    return filteredEvents
      .filter((e) => dayKeyInZone(e.timestamp, tz) === expandedDay)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [filteredEvents, expandedDay, tz]);

  const backLink = '/panel/equipo';

  if (loading) {
    return (
      <div className="h-[calc(100vh-64px-4rem)] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (forbiddenByScope) return <Navigate to="/panel/equipo" replace />;

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="mb-5">
        <Link to={backLink} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-ink transition-colors mb-3">
          <ChevronLeft size={16} /> Volver
        </Link>
        {worker ? (
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">
                Trabajador #{workerId} · {worker.type != null ? USER_TYPE_LABELS[worker.type] : 'sin tipo asignado'}
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">{worker.name}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-ink-muted">
                {worker.email && (
                  <a href={`mailto:${worker.email}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Mail size={12} /> {worker.email}
                  </a>
                )}
                {worker.phone && (
                  <a href={`tel:${worker.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Phone size={12} /> {worker.phone}
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-xs">
                {worker.workingSite != null && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 text-ink-muted">
                    <MapPin size={11} /> {workplace?.name || `Sede #${worker.workingSite}`}
                  </span>
                )}
                {worker.geoTracking && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-soft text-primary-darker">
                    <LocateFixed size={11} /> GPS requerido
                  </span>
                )}
                {worker.worksRemotely ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-success/10 text-success">
                    <Wifi size={11} /> Remoto
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 text-ink-muted">
                    <WifiOff size={11} /> Presencial
                  </span>
                )}
                {worker.canRequestCheckins && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-warning/10 text-warning">
                    <Clock size={11} /> Fichaje diferido
                  </span>
                )}
                {/* Inicio de cómputo: EXCEPCIONAL y opcional — casi ningún
                    trabajador lo necesita. Por eso es un enlace discreto (no
                    una tarjeta ni un aviso) y no se marca de ninguna forma a
                    los trabajadores que no lo tienen. Si está puesto, se ve la
                    fecha; si no, solo los gestores ven el enlace para ponerla.
                    Sin fecha, la bolsa se comporta EXACTAMENTE igual que
                    siempre. */}
                {canManageWorker(user, worker) ? (
                  <button
                    type="button"
                    onClick={() => setComputationStartOpen(true)}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors hover:bg-slate-100 hover:text-ink ${
                      computationStart ? 'bg-slate-100 text-ink-muted tabular-nums' : 'text-ink-muted/70'
                    }`}
                    title={`${COMPUTATION_START_LABEL}: fecha desde la que cuentan las horas de este trabajador. Lo anterior queda fuera de la bolsa. Opcional: sin fecha se cuenta desde su primer fichaje.`}
                  >
                    <CalendarCheck size={11} />
                    {computationStart ? `Trabaja desde ${fmtDmy(computationStart)}` : COMPUTATION_START_LABEL}
                  </button>
                ) : computationStart ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 text-ink-muted tabular-nums"
                    title={`${COMPUTATION_START_LABEL}: las horas de este trabajador cuentan desde esta fecha; lo anterior queda fuera de la bolsa.`}
                  >
                    <CalendarCheck size={11} /> Trabaja desde {fmtDmy(computationStart)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Histórico de jornadas — visible para CUALQUIER admin con
                  acceso a la ficha. Es solo consulta: no escribe en backend.
                  Se mantiene fuera del bloque `canManageWorker` para que
                  inspectores y supervisores también puedan revisarlo. */}
              <button
                onClick={() => setScheduleHistoryOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-ink-muted border border-slate-200 hover:text-ink hover:bg-slate-50 transition-colors"
                title="Ver el histórico de jornadas (horarios) que ha tenido este trabajador"
              >
                <History size={14} /> Histórico de jornadas
              </button>
              {canManageWorker(user, worker) && (
                <>
                  {/* Histórico de convenios — solo para gestores. Permite
                      crear tramos con periodo cerrado (validFrom+validTo) y
                      eliminar asignaciones existentes. */}
                  <button
                    onClick={() => setAgreementHistoryOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-ink-muted border border-slate-200 hover:text-ink hover:bg-slate-50 transition-colors"
                    title="Ver y gestionar el histórico de convenios del trabajador (puedes añadir tramos históricos cerrados)"
                  >
                    <FileText size={14} /> Histórico de convenios
                  </button>
                  <button
                    onClick={() => setAssignModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-dark transition-colors"
                    title="Asignar vacaciones, ausencia de día completo o un fichaje manual ya aprobado"
                  >
                    <Plus size={14} /> Asignar evento
                  </button>
                  <button
                    onClick={() => setCompModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-primary border border-primary/30 hover:bg-primary-soft transition-colors"
                    title="Crear un día de compensación aprobado para este trabajador"
                  >
                    <Gift size={14} /> Compensación
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-ink-muted">Trabajador #{workerId} no encontrado.</div>
        )}
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      {/* La sección colapsable "Histórico de convenios y horarios" se quitó
          de la página por petición del usuario — el histórico sigue
          disponible vía los botones "Histórico de convenios" e "Histórico
          de horarios" de la cabecera, que abren los modales correspondientes.
          assignmentHistory y scheduleHistory se siguen cargando porque los
          usa el cómputo time-aware de la bolsa de horas y otras pantallas. */}

      <section className="pb-5 mb-5 border-b border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Clock size={11} className="text-primary" /> Horas en el período
            </div>
            <div className="font-display text-2xl font-bold text-primary-darker tabular-nums">{fmtHours(stats.totalMinutes)}</div>
            <div className="text-xs text-ink-muted mt-0.5" title="Todas las pausas se restan del tiempo trabajado">
              {fmtHours(stats.pauseMinutes)} de pausas descontadas
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <CalIcon size={11} className="text-success" /> Días fichados
            </div>
            <div className="font-display text-2xl font-bold text-success tabular-nums">{stats.workedDays}</div>
            <div className="text-xs text-ink-muted mt-0.5">{stats.completeDays} completos</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Plane size={11} className="text-accent-dark" /> Vacaciones {countYear}
            </div>
            <div
              className="font-display text-2xl font-bold text-accent-dark tabular-nums"
              title={vacationCountsReady ? undefined : (workhoursFailed ? 'No se pudo cargar el horario del trabajador' : 'Sin horario configurado: no se pueden calcular los días')}
            >
              {vacationDays}
            </div>
            <div className="text-xs text-ink-muted mt-0.5">
              días {holidayType === 'festivos' ? 'naturales' : 'laborables'} · {approvedVacationPeriods.length} aprobado{approvedVacationPeriods.length === 1 ? '' : 's'}
              {pendingVacationPeriods.length > 0 && (
                <> · <span className="text-amber-600 font-semibold">{pendingVacationPeriods.length} pendiente{pendingVacationPeriods.length === 1 ? '' : 's'}</span></>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <UserMinus size={11} className="text-danger" /> Días completos {countYear}
            </div>
            <div
              className="font-display text-2xl font-bold text-danger tabular-nums"
              title={sickCountsReady ? undefined : (workhoursFailed ? 'No se pudo cargar el horario del trabajador' : 'Sin horario configurado: no se pueden calcular los días')}
            >
              {sickDays}
            </div>
            {/* Las bajas SIEMPRE se computan en modo 'festivos' (naturales)
                — es la regla del INSS, ver línea ~421. La etiqueta tiene
                que reflejarlo para no engañar al admin. */}
            <div className="text-xs text-ink-muted mt-0.5">
              días naturales · {approvedSickPeriods.length} aprobado{approvedSickPeriods.length === 1 ? '' : 's'}
              {pendingSickPeriods.length > 0 && (
                <> · <span className="text-amber-600 font-semibold">{pendingSickPeriods.length} pendiente{pendingSickPeriods.length === 1 ? '' : 's'}</span></>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_320px] gap-6 overflow-hidden">
        <section className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-2">
              <TrendingUp size={14} /> Historial de fichajes
              {tzHint && <span className="normal-case tracking-normal font-normal text-[11px]">· {tzHint}</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <button onClick={() => applyQuickRange(1)} className="px-2.5 py-1 rounded-md text-xs font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">1 mes</button>
                <button onClick={() => applyQuickRange(3)} className="px-2.5 py-1 rounded-md text-xs font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">3 meses</button>
                <button onClick={() => applyQuickRange(12)} className="px-2.5 py-1 rounded-md text-xs font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">1 año</button>
                <button onClick={() => applyQuickRange(0)} className="px-2.5 py-1 rounded-md text-xs font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">Todo</button>
              </div>
              <input
                type="date"
                value={draftStart}
                max={draftEnd}
                onChange={(e) => setDraftStart(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && draftDirty) applyDraftRange(); }}
                className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
              />
              <span className="text-xs text-ink-muted">→</span>
              <input
                type="date"
                value={draftEnd}
                min={draftStart}
                onChange={(e) => setDraftEnd(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && draftDirty) applyDraftRange(); }}
                className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={applyDraftRange}
                disabled={!draftDirty}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                  draftDirty
                    ? 'bg-primary text-white hover:bg-primary-dark'
                    : 'bg-slate-100 text-ink-muted/60 cursor-not-allowed'
                }`}
                title={draftDirty ? 'Aplicar rango y recargar historial' : 'El rango actual ya está aplicado'}
              >
                <Search size={12} /> Buscar
              </button>
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                  showFilters
                    ? 'border-primary text-primary-darker bg-primary-soft'
                    : 'border-slate-200 text-ink-muted hover:text-ink hover:bg-slate-50'
                }`}
                title="Más filtros (tipo de fichaje, estado…)"
              >
                <Filter size={12} /> Filtros
                {filtersActive && (
                  <span className="ml-0.5 inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </button>
              <span className="text-xs text-ink-muted tabular-nums">{statusRows.length} día{statusRows.length === 1 ? '' : 's'}</span>
            </div>
          </div>

          {/* Panel de filtros adicionales — colapsable. Por defecto jornada
              + pausas, pero el admin puede ampliar a vacaciones, bajas, etc.,
              y filtrar por estado o por si fue fichaje diferido. Los filtros
              no cambian los totales del día. */}
          {showFilters && (
            <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-2.5 mb-3 space-y-2.5">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1.5">Tipo de fichaje</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { key: 'clock', label: 'Jornada' },
                    { key: 'pause', label: 'Pausas' },
                    { key: 'vacation', label: 'Vacaciones' },
                    { key: 'sick', label: 'Días completos' },
                    { key: 'compensation', label: 'Compensaciones' },
                    { key: 'absence', label: 'Ausencias' },
                  ].map((opt) => {
                    const checked = typeGroups.has(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => toggleTypeGroup(opt.key)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          checked
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-200 bg-white text-ink-muted hover:text-ink hover:border-slate-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1">Estado</div>
                  <select
                    value={approvalFilter}
                    onChange={(e) => setApprovalFilter(e.target.value)}
                    className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary bg-white"
                  >
                    <option value="todos">Todos</option>
                    {Object.entries(APPROVAL_STATUS_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1">Modo</div>
                  <select
                    value={deferredFilter}
                    onChange={(e) => setDeferredFilter(e.target.value)}
                    className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary bg-white"
                  >
                    <option value="todos">Todos (normal + diferido)</option>
                    <option value="normal">Solo en vivo</option>
                    <option value="diferido">Solo diferidos</option>
                  </select>
                </div>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-danger px-2 py-1 rounded transition-colors"
                  >
                    <XIcon size={11} /> Restablecer filtros
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Filtro de ESTADO del día (selección única) con recuento. Se
              calcula sobre los días que deja ver el filtro de tipo. */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2" role="group" aria-label="Filtrar días por estado">
            {HISTORY_STATUS_FILTERS.map((f) => {
              const active = statusFilter === f.key;
              const n = statusCounts[f.key] || 0;
              const needsSchedule = f.key === 'sinFichar' || f.key === 'exceso' || f.key === 'defecto';
              const disabled = needsSchedule && history.noSchedule;
              const tooltip = f.key === 'exceso' || f.key === 'defecto'
                ? `${f.tooltip} (ahora ±${fmtThreshold(deviationThreshold)})`
                : f.tooltip;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setStatusFilter(f.key)}
                  disabled={disabled}
                  aria-pressed={active}
                  title={disabled
                    ? `${NO_SCHEDULE_LABEL}: no se puede comparar con la jornada`
                    : tooltip}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    active
                      ? 'border-primary bg-primary text-white'
                      : disabled
                        ? 'border-slate-200 bg-white text-ink-muted/50 cursor-not-allowed'
                        : 'border-slate-200 bg-white text-ink-muted hover:text-ink hover:border-slate-300'
                  }`}
                >
                  {f.label}
                  <span className={`tabular-nums text-[10px] px-1.5 rounded-full ${
                    active ? 'bg-white/20 text-white' : n > 0 && f.key !== 'todos' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-ink-muted'
                  }`}
                  >
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Umbral de desviación + leyenda de colores de las filas. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-2 text-[11px] text-ink-muted">
            <div className="inline-flex items-center gap-1.5">
              <span className="font-semibold uppercase tracking-wider text-[10px]" id="deviation-threshold-label">Desviación</span>
              <div
                className="inline-flex rounded-md border border-slate-200 bg-white p-0.5"
                role="radiogroup"
                aria-labelledby="deviation-threshold-label"
              >
                {DEVIATION_THRESHOLDS.map((m) => {
                  const on = deviationThreshold === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={history.noSchedule}
                      onClick={() => setDeviationThreshold(m)}
                      title={`Marcar los días que se pasan o no llegan a su jornada en ${fmtThreshold(m)} o más`}
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold tabular-nums transition-colors ${
                        on ? 'bg-primary text-white' : 'text-ink-muted hover:text-ink disabled:hover:text-ink-muted'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {fmtThreshold(m)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="Leyenda de colores">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm border border-amber-400 bg-amber-50" aria-hidden="true" />
                Sin cerrar / sin abrir
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm border border-rose-400 bg-rose-50" aria-hidden="true" />
                No llega (incl. sin fichar)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm border border-sky-400 bg-sky-50" aria-hidden="true" />
                Se pasa
              </span>
              <span className="text-ink-muted/80">
                {history.noSchedule
                  ? 'Sin horario: no se compara con la jornada.'
                  : `Se marca cuando la diferencia con su jornada supera ±${fmtThreshold(deviationThreshold)}.`}
              </span>
            </div>
          </div>
          {history.noSchedule && (
            <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <span>
                <strong>{NO_SCHEDULE_LABEL}.</strong> No se calcula la jornada esperada ni la diferencia, y no se pueden
                detectar los días sin fichar. Asigna un horario para verlos.
              </span>
            </div>
          )}
          <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
            {days.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-muted">
                {statusFilter === 'todos' ? 'Sin fichajes en el rango seleccionado.' : 'Ningún día cumple este filtro en el rango seleccionado.'}
              </div>
            ) : (
              <table className="w-full">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="text-xs uppercase tracking-wider text-ink-muted">
                    <th className="text-left font-semibold py-3 pr-2 w-8"></th>
                    <th className="text-left font-semibold py-3 pr-3" aria-sort={sort.key === 'date' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <SortHeader label="Día" active={sort.key === 'date'} dir={sort.dir} onClick={() => toggleSort('date')} title="Ordenar por fecha" />
                    </th>
                    <th className="text-left font-semibold py-3 pr-3">Entrada</th>
                    <th className="text-left font-semibold py-3 pr-3">Salida</th>
                    <th className="text-right font-semibold py-3 pr-3"># fichajes</th>
                    <th className="text-right font-semibold py-3 pr-3" title="Tiempo total de pausas del día (se resta del total)">Pausas</th>
                    <th className="text-right font-semibold py-3 pr-3" aria-sort={sort.key === 'worked' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <SortHeader label="Total" align="right" active={sort.key === 'worked'} dir={sort.dir} onClick={() => toggleSort('worked')} title="Ordenar por total trabajado" />
                    </th>
                    <th className="text-right font-semibold py-3 pr-3" title="Jornada esperada según el horario vigente ese día, menos festivos, vacaciones, días completos, compensaciones y ausencias por horas aprobadas (mismas reglas que la bolsa)">Esperado</th>
                    <th className="text-right font-semibold py-3 pr-3" aria-sort={sort.key === 'balance' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <SortHeader label="Diferencia" align="right" active={sort.key === 'balance'} dir={sort.dir} onClick={() => toggleSort('balance')} title="Ordenar por diferencia (trabajado − esperado). Ascendente: primero los días que restan en la bolsa" />
                    </th>
                    <th className="text-left font-semibold py-3 pr-2">Estado</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {days.map((d) => {
                    const isOpen = expandedDay === d.date;
                    const pauseWarnings = pauseWarningsByEvent(d.work);
                    const missing = d.flags.sinFichar;
                    const deviation = classifyDeviation(d, deviationThreshold);
                    const tint = ROW_TINT[rowHighlight(d, deviationThreshold)] || null;
                    const bar = tint ? tint.bar : '';
                    let rowBg = 'hover:bg-white';
                    if (isOpen) rowBg = 'bg-primary-soft/30';
                    else if (tint) rowBg = tint.row;
                    const expectedTitle = d.expectedMinutes == null
                      ? NO_SCHEDULE_LABEL
                      : [
                        d.dayType === 'ausencia'
                          ? `${DAY_TYPE_LABELS.ausencia} (${formatHm(d.hourlyAbsenceMinutes)} h): se recupera, la jornada no se reduce`
                          : d.dayType === 'compensacionParcial'
                            ? `${DAY_TYPE_LABELS.compensacionParcial} (${formatHm(d.compensationMinutes)} h): no cuentan como trabajadas ni reducen la jornada — salen de su bolsa`
                            : (d.dayType ? DAY_TYPE_LABELS[d.dayType] : null),
                        d.isToday ? 'Hoy: aún no cuenta en la bolsa' : null,
                        d.isFuture ? 'Día futuro: no cuenta en la bolsa' : null,
                        !d.inBalancePeriod && !d.isToday && !d.isFuture ? 'Fuera del periodo de cómputo de la bolsa' : null,
                      ].filter(Boolean).join(' · ');
                    return (
                      <React.Fragment key={d.date}>
                        <tr
                          className={`border-t border-slate-100 transition-colors cursor-pointer ${rowBg}`}
                          onClick={() => setExpandedDay(isOpen ? null : d.date)}
                        >
                          <td className={`py-2.5 pl-1.5 pr-2 text-ink-muted ${bar}`}>
                            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </td>
                          <td className="py-2.5 pr-3 font-medium text-ink">
                            <span className="capitalize">{fmtDay(d.date)}</span>
                            {d.dayType && d.dayType !== 'laborable' && (
                              <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                                {DAY_TYPE_LABELS[d.dayType]}
                              </span>
                            )}
                          </td>
                          {missing && d.events.length === 0 ? (
                            <td colSpan={3} className="py-2.5 pr-3 text-xs italic text-danger">
                              Sin fichajes · se esperaban {formatHm(d.expectedMinutes)} h
                            </td>
                          ) : (
                            <>
                              <td className="py-2.5 pr-3 tabular-nums text-success font-semibold">
                                {fmtTime(d.firstIn, tz) || <span className="text-ink-muted font-normal">—</span>}
                              </td>
                              <td className="py-2.5 pr-3 tabular-nums text-primary-darker font-semibold">
                                {fmtTime(d.lastOut, tz) || <span className="text-ink-muted font-normal">—</span>}
                              </td>
                              <td className="py-2.5 pr-3 text-right tabular-nums text-ink-muted text-xs">
                                {d.clockIns} entrada{d.clockIns === 1 ? '' : 's'} · {d.clockOuts} salida{d.clockOuts === 1 ? '' : 's'}
                              </td>
                            </>
                          )}
                          <td className="py-2.5 pr-3 text-right tabular-nums text-warning font-medium">
                            {d.pauseMinutes > 0 ? fmtHours(d.pauseMinutes) : <span className="text-ink-muted font-normal">—</span>}
                            {d.work.hasPauseWarning && (
                              <span
                                className="ml-1.5 inline-flex items-center align-middle text-warning"
                                title="Pausa sin cerrar o sin inicio: revisa los fichajes de este día"
                              >
                                <AlertTriangle size={12} />
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums font-bold text-ink">
                            {d.workedMinutes > 0 ? fmtHours(d.workedMinutes) : (d.enCurso ? 'En curso' : <span className="text-ink-muted font-normal">—</span>)}
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-ink-muted" title={expectedTitle}>
                            {d.expectedMinutes == null ? '—' : formatHm(d.expectedMinutes)}
                          </td>
                          <td
                            className={`py-2.5 pr-3 text-right tabular-nums font-bold ${balanceCls(d.balanceMinutes)}`}
                            title={d.balanceMinutes == null ? expectedTitle : `Trabajado ${formatHm(d.workedMinutes)} − esperado ${formatHm(d.expectedMinutes)}`}
                          >
                            {d.balanceMinutes == null ? '—' : formatSignedHm(d.balanceMinutes)}
                          </td>
                          <td className="py-2.5 pr-2">
                            <div className="flex flex-wrap gap-1">
                              {['sinFichar', 'sinCerrar', 'sinAbrir', 'porAprobar'].filter((k) => d.flags[k]).map((k) => (
                                <span
                                  key={k}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${FLAG_BADGE_CLS[k]}`}
                                  title={FLAG_INFO[k].tooltip}
                                >
                                  {FLAG_INFO[k].label}
                                </span>
                              ))}
                              {/* Se pasa / No llega (no se repite en "Sin fichar"). */}
                              {deviation && !missing && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${FLAG_BADGE_CLS[deviation]}`}
                                  title={`Diferencia con su jornada de ${formatSignedHm(d.balanceMinutes)} h (umbral ±${fmtThreshold(deviationThreshold)})`}
                                >
                                  {deviation === 'exceso' ? 'Se pasa' : 'No llega'}
                                  <span className="ml-1 tabular-nums normal-case tracking-normal">{formatSignedHm(d.balanceMinutes)}</span>
                                </span>
                              )}
                              {d.otherWarnings.length > 0 && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${FLAG_BADGE_CLS.otras}`}
                                  title={`${FLAG_INFO.otras.tooltip}\n${d.otherWarnings.map((w) => OTHER_WARNING_LABELS[w] || w).join(', ')}`}
                                >
                                  {FLAG_INFO.otras.label}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isOpen && expandedDayEvents.length === 0 && (
                          <tr className="border-t border-slate-100 bg-primary-soft/10">
                            <td className={bar}></td>
                            <td colSpan={9} className="py-3 pr-4 text-xs text-ink-muted">
                              {d.events.length === 0
                                ? `Sin fichajes este día. ${d.expectedMinutes ? `Jornada esperada: ${formatHm(d.expectedMinutes)} h (horario vigente ese día); la bolsa la descuenta como falta.` : ''}`
                                : 'Ningún fichaje de este día coincide con los filtros de tipo/estado.'}
                            </td>
                          </tr>
                        )}
                        {isOpen && expandedDayEvents.length > 0 && (
                          <tr className="border-t border-slate-100 bg-primary-soft/10">
                            <td className={bar}></td>
                            <td colSpan={9} className="py-3 pr-4">
                              <div className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold mb-2">
                                Detalle de fichajes <span className="text-ink-muted/60 font-normal normal-case">({expandedDayEvents.length})</span>
                              </div>
                              <ul className="space-y-1.5">
                                {expandedDayEvents.map((e) => {
                                  const c = parseGeoInfo(e.geoInfo);
                                  const isSelected = selectedIds.has(e.id);
                                  const isClock = e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT;
                                  const isPause = e.eventType === EVENT_TYPES.PAUSE_START || e.eventType === EVENT_TYPES.PAUSE_END;
                                  // Ubicación: la mostramos para fichajes de jornada Y para pausas
                                  // (ambos capturan geoInfo al fichar). Así se ve desde dónde se
                                  // inició/volvió de la pausa, igual que en entrada y salida.
                                  const showLocation = isClock || isPause;
                                  // Color del badge según familia del evento.
                                  let badgeCls = 'bg-slate-100 text-ink-muted';
                                  if (e.eventType === EVENT_TYPES.CLOCK_IN) badgeCls = 'bg-success/10 text-success';
                                  else if (e.eventType === EVENT_TYPES.CLOCK_OUT) badgeCls = 'bg-primary-soft text-primary-darker';
                                  else if (e.eventType === EVENT_TYPES.PAUSE_START || e.eventType === EVENT_TYPES.PAUSE_END) badgeCls = 'bg-amber-100 text-amber-800';
                                  else if (e.eventType === EVENT_TYPES.VACATION_START || e.eventType === EVENT_TYPES.VACATION_END) badgeCls = 'bg-sky-100 text-sky-800';
                                  else if (e.eventType === EVENT_TYPES.SICK_LEAVE_START || e.eventType === EVENT_TYPES.SICK_LEAVE_END) badgeCls = 'bg-danger/10 text-danger';
                                  else if (e.eventType === EVENT_TYPES.COMPENSATION_START || e.eventType === EVENT_TYPES.COMPENSATION_END) badgeCls = 'bg-violet-100 text-violet-800';
                                  else if (e.eventType === EVENT_TYPES.ABSENCE_START || e.eventType === EVENT_TYPES.ABSENCE_END) badgeCls = 'bg-orange-100 text-orange-800';
                                  // Estado de aprobación: mostramos el chip si NO está aprobado
                                  // (el caso normal es aprobado y no aporta info visual).
                                  const status = Number(e.approvalStatus ?? APPROVAL_STATUS.APPROVED);
                                  // Audit trail visible: "Aprobado por X" / "Editado por X" / "Motivo".
                                  // Solo enseñamos el aprobador en eventos supervisados (vacaciones,
                                  // bajas, ausencias, compensaciones) o diferidos. El editor y el
                                  // motivo aplican a cualquier tipo si tienen flag en geoInfo.
                                  const showApprover = SUPERVISED_TYPES.has(e.eventType) || isDeferred(e);
                                  const apprText = showApprover ? approverLabel(e) : null;
                                  const modText = modifierLabel(e);
                                  const motivo = parseMotivo(e.geoInfo);
                                  // Flag de recuperable en ausencias justificadas — el admin
                                  // lo elige al aprobar. true → horas descontadas de la bolsa.
                                  // Por horas: se recuperan SIEMPRE (2026-09-22), aunque
                                  // un aprobado antiguo diga ;recoverable=0.
                                  const isHourlyAbsence = e.eventType === EVENT_TYPES.ABSENCE_START || e.eventType === EVENT_TYPES.ABSENCE_END;
                                  const recoverable = isHourlyAbsence && status === APPROVAL_STATUS.APPROVED
                                    ? true
                                    : parseRecoverableFlag(e.geoInfo);
                                  return (
                                  <li key={e.id || e.timestamp} className="flex items-center gap-3 text-xs flex-wrap">
                                    {canDelete && isClock && (
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(ev) => { ev.stopPropagation(); toggleSelect(e.id); }}
                                        onClick={(ev) => ev.stopPropagation()}
                                        className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                                        title="Seleccionar para eliminar"
                                      />
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full font-semibold ${badgeCls}`}>
                                      {EVENT_TYPE_LABELS[e.eventType] || `Tipo ${e.eventType}`}
                                    </span>
                                    <span className="tabular-nums font-mono text-ink">{fmtTime(e.timestamp, tz)}</span>
                                    {pauseWarnings.has(e) && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                                        title="Esta pausa no se empareja bien con el resto de fichajes del día"
                                      >
                                        <AlertTriangle size={10} /> {PAUSE_WARNING_LABELS[pauseWarnings.get(e)]}
                                      </span>
                                    )}
                                    {status !== APPROVAL_STATUS.APPROVED && (
                                      <span
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                          status === APPROVAL_STATUS.PENDING ? 'bg-amber-100 text-amber-800' : 'bg-danger/10 text-danger'
                                        }`}
                                        title={APPROVAL_STATUS_LABELS[status]}
                                      >
                                        {APPROVAL_STATUS_LABELS[status]}
                                      </span>
                                    )}
                                    {isDeferred(e) && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider shrink-0"
                                        title="Fichaje registrado a posteriori (diferido). La fecha de la derecha es cuando quedó guardado."
                                      >
                                        Diferido · <span className="font-mono normal-case tracking-normal">{deferredLabel(e)}</span>
                                      </span>
                                    )}
                                    {/* Audit trail visible. Mismo patrón que en el Informe horario:
                                        verde si aprobado, rojo si rechazado, violeta para editado.
                                        Si no hay traza (eventos antiguos), no se muestra nada. */}
                                    {showApprover && apprText && status === APPROVAL_STATUS.APPROVED && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px] font-semibold">
                                        <ShieldCheck size={10} />
                                        {apprText}
                                      </span>
                                    )}
                                    {showApprover && apprText && status === APPROVAL_STATUS.REJECTED && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-danger/10 text-danger text-[10px] font-semibold">
                                        <ShieldCheck size={10} />
                                        {apprText}
                                      </span>
                                    )}
                                    {modText && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-semibold">
                                        <Pencil size={10} />
                                        {modText}
                                      </span>
                                    )}
                                    {recoverable === true && (
                                      <span
                                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                                        title={isHourlyAbsence
                                          ? 'Ausencia por horas: se recupera. La jornada del día no se reduce, así que las horas ausentes salen como deuda en la bolsa.'
                                          : 'Las horas de esta ausencia se descontaron de la bolsa: el trabajador las debe a la empresa.'}
                                      >
                                        Recuperable
                                      </span>
                                    )}
                                    {recoverable === false && (
                                      <span
                                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px] font-bold uppercase tracking-wider"
                                        title="La empresa asume la ausencia: no se descontó de la bolsa de horas."
                                      >
                                        No recuperable
                                      </span>
                                    )}
                                    {motivo && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-ink text-[10px]"
                                        title={motivo}
                                      >
                                        <span className="font-semibold text-ink-muted">Motivo:</span>
                                        <span className="truncate max-w-[200px]">{motivo}</span>
                                      </span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      {showLocation && <LocationCell geoInfo={e.geoInfo} workplace={workplace} />}
                                    </div>
                                    {c && showLocation && (
                                      <a
                                        href={`https://www.google.com/maps?q=${c.lat},${c.lng}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="tabular-nums text-[11px] text-primary-darker hover:text-primary underline shrink-0"
                                        title="Abrir en Google Maps"
                                      >
                                        {c.lat.toFixed(5)},{c.lng.toFixed(5)}
                                      </a>
                                    )}
                                    {/* Edit y Delete: usan el mismo permiso
                                        (canDelete = canDeleteEvent). Si el
                                        usuario lo tiene, puede ambas. */}
                                    {canDelete && (
                                      <>
                                        <button
                                          onClick={(ev) => { ev.stopPropagation(); setEditingEvent(e); }}
                                          className="text-ink-muted hover:text-primary-darker p-1 rounded transition shrink-0"
                                          title="Editar hora y tipo de este fichaje"
                                        >
                                          <Pencil size={12} />
                                        </button>
                                        <button
                                          onClick={(ev) => { ev.stopPropagation(); openSingleDelete([e]); }}
                                          className="text-red-500 hover:text-red-700 p-1 rounded transition shrink-0"
                                          title="Eliminar este fichaje"
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      </>
                                    )}
                                  </li>
                                  );
                                })}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {sortedRows.length > days.length && (
              <div className="py-3 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((n) => n + HISTORY_PAGE_SIZE)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold text-primary-darker border border-primary/30 hover:bg-primary-soft transition-colors"
                >
                  Mostrar más ({days.length} de {sortedRows.length})
                </button>
              </div>
            )}
          </div>

          {/* Resumen de los días filtrados + conciliación con la bolsa. */}
          <div className="border-t border-slate-200 pt-2 mt-1 text-xs text-ink-muted space-y-1">
            <div className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
              <span title={statusFilter === 'todos' ? 'Días del rango (según el filtro de tipo)' : 'Días que cumplen el filtro de estado'}>
                <strong className="text-ink">{visibleSummary.days}</strong> día{visibleSummary.days === 1 ? '' : 's'}
                {statusFilter !== 'todos' && ' (filtrados)'}
              </span>
              <span>Trabajado <strong className="text-ink">{fmtHours(visibleSummary.worked)}</strong></span>
              <span title="Hoy y los días futuros no suman jornada esperada">
                Esperado <strong className="text-ink">{visibleSummary.expected == null ? '—' : `${formatHm(visibleSummary.expected)} h`}</strong>
              </span>
              <span>
                Diferencia{' '}
                <strong className={balanceCls(visibleSummary.balance)}>
                  {visibleSummary.balance == null ? '—' : `${formatSignedHm(visibleSummary.balance)} h`}
                </strong>
              </span>
              {!history.noSchedule && (
                <span title={`Días que se pasan / no llegan a su jornada en ±${fmtThreshold(deviationThreshold)} o más (no llega incluye los días sin fichar)`}>
                  Se pasa <strong className="text-sky-700">{visibleSummary.exceso}</strong>
                  {' · '}
                  No llega <strong className="text-rose-700">{visibleSummary.defecto}</strong>
                  <span className="text-ink-muted/80"> (±{fmtThreshold(deviationThreshold)})</span>
                </span>
              )}
            </div>
            {!history.noSchedule && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {allYearEventsError ? (
                  <span className="text-danger">No se pudo cargar la bolsa para conciliar.</span>
                ) : !reconciliation ? (
                  <span>La bolsa automática aún no tiene periodo de cómputo (sin fichajes desde el {fmtDmy(BALANCE_EPOCH_ISO)}).</span>
                ) : (
                  <>
                    <span title="Bolsa automática: sin ajustes manuales">
                      Bolsa automática en su periodo ({fmtDmy(reconciliation.startKey)}–{fmtDmy(reconciliation.endKey)}):{' '}
                      <strong className={balanceCls(reconciliation.fullTotal)}>{formatSignedHm(reconciliation.fullTotal)} h</strong>
                    </span>
                    {!reconciliation.overlap ? (
                      <span>· el rango del historial no incluye ese periodo</span>
                    ) : (
                      <>
                        {!reconciliation.covers && (
                          <span>
                            · en el tramo visible ({fmtDmy(reconciliation.coverStart)}–{fmtDmy(reconciliation.coverEnd)}):{' '}
                            <strong className={balanceCls(reconciliation.bolsaMinutes)}>{formatSignedHm(reconciliation.bolsaMinutes)} h</strong>
                          </span>
                        )}
                        <span title="Suma de la columna Diferencia de TODOS los días de ese periodo (sin filtros de estado ni de tipo)">
                          · Suma de la tabla en ese periodo:{' '}
                          <strong className={balanceCls(reconciliation.tableMinutes)}>{formatSignedHm(reconciliation.tableMinutes)} h</strong>
                        </span>
                        {reconciliation.ok ? (
                          <span className="inline-flex items-center gap-1 text-success font-semibold" title="La tabla cuadra con la bolsa">
                            <CheckCircle2 size={12} /> cuadra
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-danger font-semibold" title="La tabla y la bolsa no coinciden: recarga la ficha; si persiste, avisa a soporte">
                            <AlertTriangle size={12} /> diferencia {formatSignedHm(reconciliation.diff)} h
                          </span>
                        )}
                      </>
                    )}
                    {(histStart !== reconciliation.startKey || histEnd !== reconciliation.endKey) && (
                      <button
                        type="button"
                        onClick={applyBalancePeriod}
                        className="ml-1 px-2 py-0.5 rounded-md text-xs font-semibold text-primary-darker border border-primary/30 hover:bg-primary-soft transition-colors"
                        title="Mostrar en el historial exactamente el periodo que computa la bolsa"
                      >
                        Ver periodo de la bolsa
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        <aside className="flex flex-col min-h-0 gap-5 overflow-y-auto pr-1">
          <HourBalanceCard
            worker={worker}
            events={allYearEvents}
            // Si hay histórico de schedule versions (38h/sem → 40h/sem,
            // etc.) pasamos el array enriquecido — sin enriquecimiento,
            // las versiones del backend vienen sin assignSlots y el
            // cómputo daría +N horas falsas (cada día con worked>0 y
            // expected=0 suma todo el tiempo trabajado como extra).
            workhours={scheduleInput}
            // Festivos del trabajador (Set<ISO>) para que el cómputo NO
            // los marque como faltas si el trabajador no fichó ese día.
            holidaysSet={workerHolidays}
            eventsError={allYearEventsError}
            canManage={canManageWorker(user, worker)}
            // Al cambiar la fecha de inicio de cómputo desde la cabecera, la
            // tarjeta recarga sus ajustes (de ahí saca el corte y la fila
            // informativa "Inicio de cómputo").
            refreshKey={balanceRefresh}
          />

          <div className="flex flex-col min-h-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-3 flex items-center gap-2">
              <Plane size={12} className="text-accent-dark" /> Vacaciones ({vacationPeriods.length})
            </div>
            <div className="border-t border-slate-200 max-h-48 overflow-y-auto">
              {vacationPeriods.length === 0 ? (
                <div className="py-4 text-center text-xs text-ink-muted">Sin vacaciones registradas.</div>
              ) : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {vacationPeriods.map((p, i) => (
                    <li key={i} className="py-2 flex items-center justify-between gap-2">
                      <span className="tabular-nums text-ink">
                        {fmtShort(dayKeyInZone(p.start, tz))}{p.end !== p.start && ` → ${fmtShort(dayKeyInZone(p.end, tz))}`}
                      </span>
                      <div className="flex items-center gap-2">
                        {p.open ? (
                          <span className="text-xs text-warning font-semibold">abierta</span>
                        ) : isRejectedStatus(p.approvalStatus) ? (
                          <span className="text-xs text-danger font-semibold">rechazada</span>
                        ) : isPendingStatus(p.approvalStatus) ? (
                          <span className="text-xs text-warning font-semibold">pendiente</span>
                        ) : (
                          <CheckCircle2 size={14} className="text-success" />
                        )}
                        {canDelete && (
                          <button
                            onClick={() => openSingleDelete([p.startEvent, p.endEvent].filter(Boolean))}
                            className="text-red-500 hover:text-red-700 p-1 rounded transition"
                            title="Eliminar período"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-col min-h-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-3 flex items-center gap-2">
              <UserMinus size={12} className="text-danger" /> Días completos ({sickPeriods.length})
            </div>
            <div className="border-t border-slate-200 max-h-48 overflow-y-auto">
              {sickPeriods.length === 0 ? (
                <div className="py-4 text-center text-xs text-ink-muted">Sin ausencias registradas.</div>
              ) : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {sickPeriods.map((p, i) => (
                    <li key={i} className="py-2 flex items-center justify-between gap-2">
                      <span className="tabular-nums text-ink">
                        {fmtShort(dayKeyInZone(p.start, tz))}{p.end !== p.start && ` → ${fmtShort(dayKeyInZone(p.end, tz))}`}
                      </span>
                      <div className="flex items-center gap-2">
                        {p.open ? (
                          <span className="text-xs text-warning font-semibold">abierta</span>
                        ) : isRejectedStatus(p.approvalStatus) ? (
                          <span className="text-xs text-danger font-semibold">rechazada</span>
                        ) : isPendingStatus(p.approvalStatus) ? (
                          <span className="text-xs text-warning font-semibold">pendiente</span>
                        ) : (
                          <CheckCircle2 size={14} className="text-success" />
                        )}
                        {canDelete && (
                          <button
                            onClick={() => openSingleDelete([p.startEvent, p.endEvent].filter(Boolean))}
                            className="text-red-500 hover:text-red-700 p-1 rounded transition"
                            title="Eliminar período"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>

      <AssignCompensationModal
        open={compModalOpen}
        worker={worker}
        onClose={() => setCompModalOpen(false)}
        onSubmitted={() => load()}
      />

      <AssignEventModal
        open={assignModalOpen}
        worker={worker}
        // Plan B del aviso de solape si la consulta de la ventana del día
        // falla: estos ya están cargados (recortados al rango del histórico).
        events={events}
        onClose={() => setAssignModalOpen(false)}
        onSubmitted={() => load()}
      />

      <DeleteEventsConfirmModal
        open={deleteOpen}
        events={pendingDelete}
        workerName={worker?.name}
        onClose={() => setDeleteOpen(false)}
        onDeleted={handleDeleted}
      />

      <EventEditModal
        open={!!editingEvent}
        event={editingEvent}
        timeZone={tz}
        onClose={() => setEditingEvent(null)}
        onSaved={handleEdited}
      />

      <WorkerScheduleHistoryModal
        open={scheduleHistoryOpen}
        worker={worker}
        onClose={() => setScheduleHistoryOpen(false)}
        onChanged={canManageWorker(user, worker) ? load : undefined}
      />

      <WorkerAgreementHistoryModal
        open={agreementHistoryOpen}
        worker={worker}
        onClose={() => setAgreementHistoryOpen(false)}
        onChanged={load}
      />

      <ComputationStartModal
        open={computationStartOpen}
        worker={worker}
        offsets={balanceOffsets}
        onClose={() => setComputationStartOpen(false)}
        onSaved={() => {
          // Recargamos los ajustes (de ahí sale el corte del historial) y
          // forzamos la recarga de la tarjeta de bolsa.
          reloadBalanceOffsets();
          setBalanceRefresh((n) => n + 1);
        }}
      />

      {canDelete && selectedIds.size > 0 && !deleteOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-red-300 shadow-2xl rounded-full pl-4 pr-2 py-2 flex items-center gap-3">
          <span className="text-sm font-semibold text-ink">
            {selectedIds.size} fichaje{selectedIds.size === 1 ? '' : 's'} seleccionado{selectedIds.size === 1 ? '' : 's'}
          </span>
          <button
            onClick={clearSelection}
            className="text-xs text-ink-muted hover:text-ink px-2 py-1 rounded-full transition inline-flex items-center gap-1"
          >
            <XIcon size={12} /> Cancelar
          </button>
          <button
            onClick={openBulkDelete}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5"
          >
            <Trash2 size={14} /> Eliminar
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkerDetailPage;
